import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../AppIcon';

/**
 * ChartUpgradePrompt
 * Displays an upgrade prompt overlay for truncated charts
 * 
 * Props:
 * - visibleYears: Number of years currently visible
 * - totalYears: Total years available
 * - truncatedAtYear: The year at which data is truncated
 * - compact: Show compact version (default: false)
 */
const ChartUpgradePrompt = ({ 
  visibleYears, 
  totalYears, 
  truncatedAtYear,
  compact = false 
}) => {
  // Always display as "5 years" for consistency (free tier limit)
  // visibleYears might be 6 (current year + 5 future years), but we say "5 years of projection"
  const projectionYears = 5;
  const totalProjectionYears = Math.max(totalYears - 1, projectionYears);
  const hiddenYears = totalProjectionYears - projectionYears;

  if (compact) {
    return (
      <div className="mt-3 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
        <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
          <Icon name="Lock" size={12} className="inline mr-1" />
          <span className="font-medium">Free tier limited to 5 years.</span>
          <Link to="/upgrade" className="ml-1 underline font-semibold hover:text-amber-700">
            Upgrade
          </Link>
        </p>
      </div>
    );
  }

  return (
    <div className="mt-4 p-4 bg-gradient-to-r from-amber-500/10 to-amber-600/10 border border-amber-500/30 rounded-xl shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
          <Icon name="Lock" size={20} className="text-amber-600 dark:text-amber-400" />
        </div>
        
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
            Viewing Limited Data
          </h4>
          <p className="text-sm text-amber-700 dark:text-amber-300 mb-3">
            You're currently viewing <strong>5 of {totalProjectionYears} years</strong> of projections. 
            <strong> {hiddenYears} year{hiddenYears !== 1 ? 's' : ''}</strong> of data {hiddenYears !== 1 ? 'are' : 'is'} hidden due to free tier limitations.
          </p>
          
          <Link
            to="/upgrade"
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg"
          >
            <Icon name="Sparkles" size={16} />
            <span>Upgrade to See Full {totalProjectionYears}-Year Projections</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ChartUpgradePrompt;
